import { Camelized, camelizeKeys } from "humps";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { EXPIRABLE_CUSTOM_TYPES } from "PFApp/constants/expirable_custom_types";
import { useCustomValueTags } from "PFApp/hooks/use_custom_value_tags";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { CustomValuePill, CustomValuePillProps } from "PFComponents/custom_value_pill";
import { isRankable } from "PFCore/helpers/custom_type";
import { requiresApproval, useCustomValueToString } from "PFCore/helpers/custom_value";
import { useCurrentAccount } from "PFCore/hooks/queries/account";
import { CustomType, CustomValueScope, Id, MatchCustomValue } from "PFCore/types";

import { useTextWithExpiryDateFormatter } from "./profile_custom_value_pill.utils";

type ProfileCustomValuePillProps = Omit<
  CustomValuePillProps,
  "name" | "experienceLevels" | "experienceLabelMap"
> & {
  customValue: MatchCustomValue;
  customType: CustomType;
  profileId: Id;
  showLeftIcon?: boolean;
  canEdit?: boolean;
  isUnbiasedMode?: boolean;
  allowPreview?: boolean;
};

export const ProfileCustomValuePill = ({
  customValue,
  customType,
  profileId,
  showLeftIcon = true,
  canEdit = false,
  isUnbiasedMode = false,
  showTooltip = true,
  allowPreview = true,
  ...props
}: ProfileCustomValuePillProps) => {
  const { data: currentAccount } = useCurrentAccount();
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();
  const { formatTextWithExpiryDate } = useTextWithExpiryDateFormatter();
  const { showPreview } = useCustomValuePreviewActions();
  const { getCustomValueTags } = useCustomValueTags();
  const { getCustomValueString } = useCustomValueToString();

  // TODO: [PROF-5746] Remove "camelizeKeys" when all ProfileCustomValuePill usages get camelized customValue
  const camelizedCustomValue = camelizeKeys(customValue) as Camelized<MatchCustomValue>;

  const isCustomTypeRankable = Boolean(customType && isRankable(customType));
  const isValidatedIconVisible = currentAccount.profile_view_customization.validated_skill_text;

  const isPendingApproval = requiresApproval(customType!, camelizedCustomValue);
  const isCustomTypeWithExpiryDate = EXPIRABLE_CUSTOM_TYPES.includes(customType?.name || "");
  const text = getCustomValueString(camelizedCustomValue);
  const name =
    isCustomTypeWithExpiryDate && camelizedCustomValue.expiryDate
      ? formatTextWithExpiryDate(text, camelizedCustomValue.expiryDate)
      : text;

  return (
    <CustomValuePill
      experience={showLeftIcon && isCustomTypeRankable ? camelizedCustomValue.experience : undefined}
      requiredExperience={
        showLeftIcon && isCustomTypeRankable ? camelizedCustomValue.requiredExperience : undefined
      }
      name={name}
      experienceLevels={experienceLevels}
      experienceLabelMap={experienceLabelMap}
      customTypeAriaLabel={customType?.display_as}
      isCore={camelizedCustomValue.top}
      isDevelopmental={camelizedCustomValue.developmental}
      isVerified={isValidatedIconVisible && camelizedCustomValue.assessed}
      isVerifiedCredly={isValidatedIconVisible && camelizedCustomValue.assessmentSource === "credly"}
      isVerifiedOthers={isValidatedIconVisible && camelizedCustomValue.emVerified}
      isPendingApproval={isPendingApproval}
      tags={getCustomValueTags(camelizedCustomValue.tagIds)}
      showTooltip={isCustomTypeRankable && showTooltip}
      customLeftIconName={!isCustomTypeRankable && showLeftIcon ? "check" : undefined}
      onClick={
        allowPreview
          ? () => {
              showPreview(
                {
                  scope: CustomValueScope.Profile,
                  customTypeId: customType!.id,
                  customValueId: camelizedCustomValue.id,
                  profileId,
                  canEdit,
                  isUnbiasedMode
                },
                true
              );
            }
          : undefined
      }
      {...props}
    />
  );
};
